/**
 * DEFAULT ignored action-type prefixes for the redux telemetry emitters
 * (`correlationMiddleware`, `reduxActionSpineMiddleware`,
 * `selectorDeltaTelemetryMiddleware`, `stateSnapshotTelemetryMiddleware`).
 *
 * PHILOSOPHY — emit faithfully, let consumers filter. This is a domain-agnostic
 * behavior/state telemetry framework, not a payments-only tool. Volume and
 * "noise" are a CONSUMER concern (filtered in NRQL); the producer should not
 * bake domain opinions about what's interesting into the emitter. So the default
 * excludes only genuine framework plumbing that carries no signal under ANY
 * domain and that a consumer could not meaningfully use:
 *
 *   - `@@redux-api-middleware/` — the RSAA envelope: `redux-api-middleware`'s
 *     internal signaling. The useful "API initiated" signal is emitted richer by
 *     `correlationMiddleware` as `RSAA: <endpoint>`, and the raw envelope is
 *     usually type-less anyway (skipped by the string-type floor below).
 *
 * DELIBERATELY NOT excluded (reversing an earlier, narrower payments view):
 *   - `@@router/*` — navigation IS behavior analytics.
 *   - `@@INIT` / `@@redux/INIT` — store/scope creation and reducer replacement
 *     (module federation, scoped stores) are real lifecycle signals for
 *     engineers, and mark the t=0 state baseline that replay diffs from.
 *
 * INJECTABLE — every emitter factory takes an optional `ignoredActionPrefixes`
 * so an app/domain can widen or narrow this set. The contiguity of the shared
 * `actionIndex` join key does not depend on the emitters using identical sets:
 * the spine back-fills anything `correlationMiddleware` skips, so the aggregate
 * `ReduxAction` stays gap-free as long as the tail emitters ignore no MORE
 * than the spine does.
 *
 * STRING-TYPE FLOOR — independent of this list, the tail/spine emitters skip
 * actions with no string `type`: there is no `actionType` to key a row on.
 */
export const TELEMETRY_IGNORED_ACTION_PREFIXES: readonly string[] = Object.freeze([
    '@@redux-api-middleware/' // redux-api-middleware RSAA envelope (internal signaling)
]);

/**
 * True when `typeString` starts with any ignored prefix. Defaults to
 * {@link TELEMETRY_IGNORED_ACTION_PREFIXES}; pass a custom set to override.
 * Callers apply their own falsy / absent-type policy BEFORE calling this.
 */
export const matchesIgnoredActionPrefix = (
    typeString: string,
    prefixes: readonly string[] = TELEMETRY_IGNORED_ACTION_PREFIXES
): boolean => prefixes.some((prefix) => typeString.startsWith(prefix));
